import { cn } from '@/lib/cn';

/** Status tones. Semantic only; gold is brand, never status. */
export type Tone = 'ok' | 'warn' | 'bad' | 'mut';

const text: Record<Tone, string> = {
  ok: 'text-ok',
  warn: 'text-warn',
  bad: 'text-bad',
  mut: 'text-mut',
};

const soft: Record<Tone, string> = {
  ok: 'text-ok bg-ok-soft',
  warn: 'text-warn bg-warn-soft',
  bad: 'text-bad bg-bad-soft',
  mut: 'text-mut bg-mut-soft',
};

/**
 * Verdict stamp: outlined, mono, uppercase. Chain-of-custody style.
 * Reserved for verdicts and record classifications; never metadata.
 */
export function Stamp({ tone, className, children }: { tone: Tone; className?: string; children: React.ReactNode }) {
  return <span className={cn('stamp', text[tone], className)}>{children}</span>;
}

/** Soft pill for non-verdict status metadata (counts, liveness chips). */
export function Pill({ tone, className, children }: { tone: Tone; className?: string; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-caption font-medium',
        soft[tone],
        className,
      )}
    >
      <i className={cn('h-1.5 w-1.5 rounded-full bg-current')} aria-hidden="true" />
      {children}
    </span>
  );
}

/**
 * Shape-coded status marker. Never color alone:
 * ok = circle, warn = triangle, bad = diamond, mut = hollow ring.
 */
const shapeStyle: Record<Tone, React.CSSProperties> = {
  ok: {},
  warn: { clipPath: 'polygon(50% 0, 100% 100%, 0 100%)', borderRadius: 0 },
  bad: { transform: 'rotate(45deg)', borderRadius: 2, width: 7, height: 7 },
  mut: {},
};

/**
 * What each dot says to a screen reader when its caller names nothing.
 *
 * `mut` is the neutral tone: no state has been reported for this thing. It
 * announced "processing", so every idle pipeline in the rail claimed to be
 * working, which is a state the app had not been told. A caller that does mean
 * processing passes `label` and says so.
 */
const shapeLabel: Record<Tone, string> = {
  ok: 'validated',
  warn: 'attention',
  bad: 'failed',
  mut: 'no state reported',
};

export function StatusDot({ tone, label, className }: { tone: Tone; label?: string; className?: string }) {
  return (
    <span
      role="img"
      aria-label={label ?? shapeLabel[tone]}
      className={cn(
        'inline-block h-2 w-2 shrink-0 rounded-full',
        tone === 'mut' ? 'border-[1.5px] border-ink-3 bg-transparent' : 'bg-current',
        text[tone],
        className,
      )}
      style={shapeStyle[tone]}
    />
  );
}
